import { assignTableAPIs, type TableFeature } from '@tanstack/react-table';

import type { VisualVariantFeatureConstructors } from './visual-variants-types.js';
import { DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT } from '../../constants.js';

type VerticalAlignment = 'top' | 'center' | 'bottom';

/**
 * Typeguard function to make sure a passed vertical alignment is an extended config.
 * @internal
 */
function isVerticalAlignmentExtendedConfig(
  verticalAlignment?:
    | VerticalAlignment
    | {
        header: VerticalAlignment;
        body: VerticalAlignment;
      },
): verticalAlignment is {
  header: VerticalAlignment;
  body: VerticalAlignment;
} {
  if (
    verticalAlignment !== undefined &&
    typeof verticalAlignment !== 'string'
  ) {
    return true;
  }

  return false;
}

/**
 * Allow accessing the body vertical alignment from the table instance, so it can be used in other features or for custom logic in the table rendering.
 */
export function table_getBodyVerticalAlignment(
  verticalAlignment:
    | VerticalAlignment
    | {
        header: VerticalAlignment;
        body: VerticalAlignment;
      },
): VerticalAlignment {
  if (isVerticalAlignmentExtendedConfig(verticalAlignment)) {
    return verticalAlignment['body'];
  }

  return verticalAlignment;
}

/**
 * Allow accessing the header vertical alignment from the table instance, so it can be used in other features or for custom logic in the table rendering.
 */
export function table_getHeaderVerticalAlignment(
  verticalAlignment:
    | VerticalAlignment
    | {
        header: VerticalAlignment;
        body: VerticalAlignment;
      },
): VerticalAlignment {
  if (isVerticalAlignmentExtendedConfig(verticalAlignment)) {
    return verticalAlignment['header'];
  }

  return verticalAlignment;
}

export function constructVisualVariantFeature(): TableFeature<VisualVariantFeatureConstructors> {
  return {
    getInitialState: (initialState) => {
      return {
        visualVariantConfig: structuredClone(
          DATA_TABLE_VISUAL_VARIANT_CONFIG_DEFAULT,
        ),
        ...initialState,
      };
    },
    constructTableAPIs: (table) => {
      assignTableAPIs('DataTableVisualVariantFeature', table, {
        table_getBodyVerticalAlignment: {
          fn: (verticalAlignment) =>
            table_getBodyVerticalAlignment(verticalAlignment),
          memoDeps: () => [
            table.atoms.visualVariantConfig?.get().verticalAlignment,
          ],
        },
        table_getHeaderVerticalAlignment: {
          fn: (verticalAlignment) =>
            table_getHeaderVerticalAlignment(verticalAlignment),
          memoDeps: () => [
            table.atoms.visualVariantConfig?.get().verticalAlignment,
          ],
        },
      });
    },
  };
}

/**
 * The VisualVariant feature adds visual styling options to the table, such as row separation,
 * vertical dividers, contained layout, row density, font style, header styles, and vertical alignment.
 * These options can be set globally for the entire table or individually for specific columns.
 */
export const DataTableVisualVariantFeature = constructVisualVariantFeature();
